import { proxy, ref } from "valtio";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CaptureStatus =
  | "landing"
  | "consent"
  | "checking-geo"
  | "camera-ready"
  | "countdown"
  | "recording"
  | "paused"
  | "preview"
  | "uploading"
  | "complete"
  | "error";

export interface EventInfo {
  id: string;
  title: string;
  description: string | null;
  slug: string;
  startsAt: number;
  endsAt: number;
  geoRequired: boolean;
  geoLatitude: number | null;
  geoLongitude: number | null;
  geoRadiusMeters: number;
  maxRecordingSeconds: number;
  maxFileSizeMb: number;
  maxParticipants: number;
  organizerName: string | null;
  recordingPrompt: string | null;
  participantCount: number;
}

export interface ParticipantInfo {
  id: string;
  displayName: string | null;
  email: string | null;
  tokenHash: string;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export interface ZoomCapability {
  min: number;
  max: number;
  step: number;
}

export interface Take {
  blob: Blob;
  duration: number;
}

export interface CaptureState {
  status: CaptureStatus;
  event: EventInfo | null;
  participant: ParticipantInfo | null;
  participantToken: string | null;
  geoVerified: boolean;
  cameraFacing: "user" | "environment";
  cameraZoom: number;
  zoomCapability: ZoomCapability | null;
  recordingDuration: number;
  recordedBlob: Blob | null;
  takes: Take[];
  selectedTakeIndex: number;
  uploadProgress: number;
  uploadBytesPerSecond: number;
  uploadStartedAt: number;
  uploadTotalBytes: number;
  uploadChunksCompleted: number;
  uploadTotalChunks: number;
  error: string | null;
}

export const captureStore = proxy<CaptureState>({
  status: "landing",
  event: null,
  participant: null,
  participantToken: null,
  geoVerified: false,
  cameraFacing: "environment",
  cameraZoom: 1,
  zoomCapability: null,
  recordingDuration: 0,
  recordedBlob: null,
  takes: [],
  selectedTakeIndex: 0,
  uploadProgress: 0,
  uploadBytesPerSecond: 0,
  uploadStartedAt: 0,
  uploadTotalBytes: 0,
  uploadChunksCompleted: 0,
  uploadTotalChunks: 0,
  error: null,
});

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export function setEvent(event: EventInfo) {
  captureStore.event = event;
}

export function setParticipant(
  participant: ParticipantInfo,
  token: string,
) {
  captureStore.participant = participant;
  captureStore.participantToken = token;

  // VIZ-1089: Persist participant identity so user can resume after tab switch
  try {
    const eventId = captureStore.event?.id;
    if (eventId) {
      sessionStorage.setItem(
        `vizimix-participant-${eventId}`,
        JSON.stringify({ participant, token })
      );
    }
  } catch {
    // sessionStorage may be unavailable in some contexts
  }
}

/** VIZ-1089: Restore saved participant from sessionStorage */
export function restoreParticipant(eventId: string): boolean {
  try {
    const saved = sessionStorage.getItem(`vizimix-participant-${eventId}`);
    if (!saved) return false;

    const { participant, token } = JSON.parse(saved) as {
      participant: ParticipantInfo;
      token: string;
    };

    captureStore.participant = participant;
    captureStore.participantToken = token;
    return true;
  } catch {
    return false;
  }
}

/** VIZ-1089: Clear saved participant (after upload completes) */
export function clearPersistedParticipant(eventId: string) {
  try {
    sessionStorage.removeItem(`vizimix-participant-${eventId}`);
  } catch {
    // ignore
  }
}

export function setStatus(status: CaptureStatus) {
  captureStore.status = status;
}

export function setError(error: string) {
  captureStore.status = "error";
  captureStore.error = error;
}

export function setCameraFacing(facing: "user" | "environment") {
  captureStore.cameraFacing = facing;
}

export function setCameraZoom(zoom: number) {
  captureStore.cameraZoom = Number.isFinite(zoom) ? zoom : 1;
}

export function setZoomCapability(cap: ZoomCapability | null) {
  captureStore.zoomCapability = cap;
}

export function setUploadProgress(progress: number) {
  captureStore.uploadProgress = progress;
}

export function setUploadSpeed(bytesPerSecond: number) {
  captureStore.uploadBytesPerSecond = bytesPerSecond;
}

export function setUploadMeta(totalBytes: number) {
  captureStore.uploadStartedAt = Date.now();
  captureStore.uploadTotalBytes = totalBytes;
}

export function setUploadChunks(completed: number, total: number) {
  captureStore.uploadChunksCompleted = completed;
  captureStore.uploadTotalChunks = total;
}

export function setRecordedBlob(blob: Blob) {
  captureStore.recordedBlob = ref(blob);
}

export function setRecordingDuration(seconds: number) {
  captureStore.recordingDuration = seconds;
}

export function addTake(blob: Blob, duration: number) {
  captureStore.takes.push({ blob: ref(blob), duration });
  captureStore.selectedTakeIndex = captureStore.takes.length - 1;
}

export function setSelectedTakeIndex(index: number) {
  captureStore.selectedTakeIndex = index;
}

export function clearTakes() {
  captureStore.takes = [];
  captureStore.selectedTakeIndex = 0;
}

export function reset() {
  captureStore.status = "landing";
  captureStore.event = null;
  captureStore.participant = null;
  captureStore.participantToken = null;
  captureStore.geoVerified = false;
  captureStore.cameraFacing = "environment";
  captureStore.cameraZoom = 1;
  captureStore.zoomCapability = null;
  captureStore.recordingDuration = 0;
  captureStore.recordedBlob = null;
  captureStore.takes = [];
  captureStore.selectedTakeIndex = 0;
  captureStore.uploadProgress = 0;
  captureStore.uploadBytesPerSecond = 0;
  captureStore.uploadStartedAt = 0;
  captureStore.uploadTotalBytes = 0;
  captureStore.uploadChunksCompleted = 0;
  captureStore.uploadTotalChunks = 0;
  captureStore.error = null;
}
